import { useTranslation } from "react-i18next";
import { MessageSquare } from "lucide-react";
import { Badge } from "@/components/ui";
import { LoadingState, ErrorState, SectionCard } from "@/components/common";
import { EmptyState } from "@/components/ui";
import { HandshakeSellerChat, channelLabel } from "@/components/handshake";
import { useHandshakeSessionActions } from "@/hooks/useHandshakeSessionActions";
import { useSellerHandshakeSessionDetail } from "@/hooks/useHandshakeSessionDetail";
import { BookingSellerHandshakeActions } from "./BookingSellerHandshakeActions";
import { cn } from "@/lib/utils";

/** Cap chat height on booking detail; messages scroll inside the wallpaper. */
const PARTNER_CHAT_SHELL_HEIGHT =
  "min-h-0 max-h-[min(60dvh,28rem)] flex-1";

interface BookingPartnerHandshakeSectionProps {
  sessionId: string;
  canInteract: boolean;
  activityId: string;
  partySize: number;
  className?: string;
}

export function BookingPartnerHandshakeSection({
  sessionId,
  canInteract,
  activityId,
  partySize,
  className,
}: BookingPartnerHandshakeSectionProps) {
  const { t } = useTranslation("bookings");
  const { data: session, isLoading, isError, isFetching, refetch } =
    useSellerHandshakeSessionDetail(sessionId);
  const { sendMessage, isSending } = useHandshakeSessionActions(sessionId, {
    admin: false,
  });

  if (isLoading) {
    return (
      <SectionCard
        title={t("detail.chatSectionTitle")}
        icon={MessageSquare}
        className={className}
      >
        <LoadingState message={t("handshake.loading")} />
      </SectionCard>
    );
  }

  if (isError) {
    return (
      <SectionCard
        title={t("detail.chatSectionTitle")}
        icon={MessageSquare}
        className={className}
      >
        <ErrorState
          message={t("handshake.loadError")}
          onRetry={() => void refetch()}
        />
      </SectionCard>
    );
  }

  if (!session) {
    return (
      <SectionCard
        title={t("detail.chatSectionTitle")}
        icon={MessageSquare}
        className={className}
      >
        <EmptyState
          size="sm"
          showIcon={false}
          title={t("handshake.notFound")}
        />
      </SectionCard>
    );
  }

  const customerName =
    `${session.customer.first_name} ${session.customer.last_name}`.trim();
  const isClosed = session.status !== "PENDING";

  return (
    <SectionCard
      title={t("detail.chatSectionTitle")}
      icon={MessageSquare}
      description={t("detail.chatChannelCustomer", {
        channel: channelLabel(session.customer_channel),
        name: customerName || "—",
      })}
      action={
        isClosed ? (
          <Badge variant="default" size="sm" className="font-normal">
            {t("detail.chatClosedBadge")}
          </Badge>
        ) : null
      }
      contentClassName="flex min-h-0 flex-1 flex-col space-y-0 overflow-hidden p-0 sm:p-0"
      className={cn(
        "max-h-[min(70dvh,36rem)] overflow-hidden",
        className,
      )}
    >
      <HandshakeSellerChat
        session={session}
        canInteract={canInteract}
        isSending={isSending}
        onSend={sendMessage}
        onRefresh={refetch}
        isRefreshing={isFetching && !isLoading}
        hideHeader
        minHeightClassName={PARTNER_CHAT_SHELL_HEIGHT}
        className="min-h-0 overflow-hidden"
        afterFooter={
          session.status === "CONFIRMED" ? (
            <BookingSellerHandshakeActions
              sessionId={sessionId}
              canInteract={canInteract}
              activityId={activityId}
              partySize={partySize}
              chatFooter
            />
          ) : null
        }
      />
      {session.status === "PENDING" ? (
        <div className="shrink-0 border-t border-surface-100 px-4 py-3 sm:px-5">
          <BookingSellerHandshakeActions
            sessionId={sessionId}
            canInteract={canInteract}
            activityId={activityId}
            partySize={partySize}
            compact
            pendingConfirm={{
              visitAt: session.context.visit_at,
              timeSlot: session.context.time_slot,
              activityRegionCode: session.context.activity_region_code,
              availabilityMode: session.context.availability_mode,
            }}
          />
        </div>
      ) : null}
    </SectionCard>
  );
}
